import { useGLTF } from '@react-three/drei';
import { useEffect, useMemo } from 'react';
import { Bone, Color, Material, Object3D, Skeleton, SkinnedMesh } from 'three';
import { SkeletonUtils } from 'three-stdlib';
import { GLTFErrorBoundary } from './GLTFErrorBoundary';
import { toonifyMaterial } from './ToonMaterial';
import { makeNPROutlineMaterial } from './NPRMaterial';

interface Props {
  /** Body root Object3D — must contain the bones we re-bind to. */
  parent: Object3D | null;
  /** Path to the equipment GLB. The GLB must export the same Bip01 armature
   *  + a SkinnedMesh weighted to those bones. (See `authoring/`.) */
  assetPath: string;
  /** Optional uniform tint LERPed into the materials (rarity / set palette). */
  tintColor?: string;
  /** Optional emissive override for legendary glow / elemental tints. */
  emissive?: string;
  emissiveIntensity?: number;
}

/**
 * Mounts a skinned equipment GLB on the body by RE-BINDING its skin to the
 * body's existing skeleton. Both the body GLB and the equipment GLB are
 * authored against the same Bip01 rig, so bone names match — we just swap
 * the skinned mesh's bone array to point at body's actual Bone objects.
 *
 * Result: the equipment piece animates exactly with the body. No per-rig
 * offset tuning, no procedural placement hack — the piece sits where the
 * artist put it in Blender.
 */
function SkinnedAttachmentInner({ parent, assetPath, tintColor, emissive, emissiveIntensity }: Props) {
  const { scene } = useGLTF(assetPath);

  // SkeletonUtils.clone properly duplicates SkinnedMesh.skeleton bone refs so
  // multiple instances don't share state. Memoised by source scene + asset
  // path so re-renders don't re-clone.
  const cloned = useMemo(() => SkeletonUtils.clone(scene), [scene]);

  // Track materials we own (and need to dispose) — toonification clones the
  // shipped MeshToonMaterials so tint/emissive overrides don't bleed into
  // other instances of the same asset.
  const ownedMaterials = useMemo<Material[]>(() => [], [cloned, tintColor, emissive, emissiveIntensity]);

  useEffect(() => {
    if (!parent) return;

    // Build name → Bone map from the body's armature.
    const bodyBones = new Map<string, Bone>();
    parent.traverse((c) => {
      if ((c as { isBone?: boolean }).isBone) bodyBones.set(c.name, c as Bone);
    });

    // Find every SkinnedMesh inside the cloned equipment scene. Re-target
    // each one's skeleton to use BODY's bones instead of the equipment's
    // own bones (which would otherwise sit dormant in bind pose).
    const meshesToMount: SkinnedMesh[] = [];
    cloned.traverse((c) => {
      const sm = c as SkinnedMesh;
      if (!(sm as { isSkinnedMesh?: boolean }).isSkinnedMesh) return;

      const oldSkel = sm.skeleton;
      const remappedBones: Bone[] = oldSkel.bones.map((b) => bodyBones.get(b.name) ?? b);
      const newSkel = new Skeleton(remappedBones, oldSkel.boneInverses);
      sm.skeleton = newSkel;

      // Tint / emissive override — CLONE the existing material to preserve
      // its type (MeshStandardMaterial stays MeshStandardMaterial — three.js
      // auto-attaches the skinning shader chunks based on the source). The
      // earlier toonifyMaterial swap created a fresh material that broke
      // skin binding for reasons that are still murky; clone-then-tweak is
      // the safe path.
      if (tintColor || emissive) {
        const matSrc = Array.isArray(sm.material) ? sm.material[0] : sm.material;
        if (matSrc && 'color' in matSrc) {
          const cloned = (matSrc as { clone(): Material }).clone() as Material & {
            color?: Color;
            emissive?: Color;
            emissiveIntensity?: number;
          };
          if (tintColor && cloned.color) {
            cloned.color.lerp(new Color(tintColor), 0.5);
          }
          if (emissive && cloned.emissive) {
            cloned.emissive = new Color(emissive);
            if (emissiveIntensity !== undefined) cloned.emissiveIntensity = emissiveIntensity;
          }
          sm.material = cloned;
          ownedMaterials.push(cloned);
        }
      }

      meshesToMount.push(sm);
    });

    // Detach each SkinnedMesh from the equipment's own armature (which we
    // discard) and re-parent under the body root. The equipment's bones in
    // `cloned` are now dead — only the SkinnedMesh references survive.
    const outlines: SkinnedMesh[] = [];
    for (const sm of meshesToMount) {
      sm.parent?.remove(sm);
      sm.castShadow = true;
      // Reset local transform — the mesh's deformation comes entirely from
      // bone matrices, so any non-identity local transform inherited from the
      // source GLB scene would just offset the mesh away from the body.
      sm.position.set(0, 0, 0);
      sm.quaternion.set(0, 0, 0, 1);
      sm.scale.set(1, 1, 1);
      sm.updateMatrix();
      // SkinnedMesh.boundingSphere is computed from bind pose; after
      // re-binding to body's bones the cached sphere is wrong and frustum
      // culling can drop the mesh from rendering. Disable culling on
      // re-bound equipment — the body it follows is in-frame anyway.
      sm.frustumCulled = false;

      // Toonify the source material so attachments match the body's cel-shaded
      // look. PlayerCharacter does this for the body; without it skinned
      // attachments (nail/shield/etc.) render as flat PBR instead.
      const oldMat = Array.isArray(sm.material) ? sm.material[0] : sm.material;
      if (oldMat && 'color' in oldMat && oldMat.type !== 'MeshToonMaterial') {
        const m = oldMat as Material & { color: Color; map?: import('three').Texture | null; emissive?: Color; emissiveIntensity?: number; transparent?: boolean; name?: string };
        const toon = toonifyMaterial({
          color: m.color,
          map: m.map ?? null,
          emissive: m.emissive,
          emissiveIntensity: m.emissiveIntensity,
          transparent: m.transparent,
          name: m.name,
        });
        sm.material = toon;
        ownedMaterials.push(toon);
      }

      parent.add(sm);

      // Skip outline for pure-emission meshes (eye glow, heart core, etc.) —
      // those are small accent geometries that the outline shell would engulf.
      // Detect by name pattern: "*Glow*" or "*Core*" or any material with
      // strong emissive and zero diffuse.
      const matName = (sm.material as { name?: string }).name ?? '';
      const matAny = sm.material as { color?: Color; emissive?: Color; emissiveIntensity?: number };
      const isEmissiveOnly =
        /glow|core/i.test(matName) ||
        (matAny.color && matAny.emissive &&
          matAny.color.r + matAny.color.g + matAny.color.b < 0.15 &&
          (matAny.emissiveIntensity ?? 0) > 0);
      if (isEmissiveOnly) continue;

      // NPR outline — noise-jittered hand-drawn stroke.
      // renderOrder = -1 ensures the outline renders BEFORE the inner mesh,
      // so the original mesh's depth values overwrite the outline center,
      // leaving only the silhouette ring visible.
      const outline = sm.clone() as SkinnedMesh;
      outline.name = sm.name + '__outline';
      outline.skeleton = sm.skeleton;
      outline.bindMatrix = sm.bindMatrix;
      outline.bindMatrixInverse = sm.bindMatrixInverse;
      const outlineMat = makeNPROutlineMaterial();
      outline.material = outlineMat;
      ownedMaterials.push(outlineMat);
      outline.castShadow = false;
      outline.receiveShadow = false;
      outline.frustumCulled = false;
      outline.renderOrder = -1;
      parent.add(outline);
      outlines.push(outline);
    }

    // Diagnostics retired — pipeline confirmed working with all 24 bones
    // matched and meshes rendering at body-origin. Re-enable via uncomment
    // if a future asset misbehaves.

    return () => {
      for (const sm of meshesToMount) {
        sm.parent?.remove(sm);
      }
      for (const o of outlines) {
        o.parent?.remove(o);
      }
      for (const m of ownedMaterials) m.dispose();
      ownedMaterials.length = 0;
    };
  }, [parent, cloned, tintColor, emissive, emissiveIntensity, ownedMaterials]);

  // Returns nothing into the JSX tree — the SkinnedMesh is added manually to
  // the parent in the effect above. R3F isn't tracking it, but since the
  // effect cleanup removes it on unmount, that's fine.
  return null;
}

export function SkinnedAttachment(props: Props) {
  return (
    <GLTFErrorBoundary key={props.assetPath} fallback={null}>
      <SkinnedAttachmentInner {...props} />
    </GLTFErrorBoundary>
  );
}
